import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["drawer", "backdrop", "menuButton", "closeButton"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.#syncDom()
    this.boundEscape = this.#onEscape.bind(this)
    this.boundBeforeVisit = this.#close.bind(this)
    this.boundTurboLoad = this.#close.bind(this)
    this.boundOverlayOpen = this.#onOverlayOpen.bind(this)
    this.boundDrawerLinkClick = this.#onDrawerLinkClick.bind(this)
    this.boundOutsideClick = this.#onOutsideClick.bind(this)

    document.addEventListener("keydown", this.boundEscape)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.addEventListener("turbo:load", this.boundTurboLoad)
    document.addEventListener("overlay:open", this.boundOverlayOpen)
    this.element.addEventListener("click", this.boundDrawerLinkClick)

    this.#syncDom()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundEscape)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.removeEventListener("turbo:load", this.boundTurboLoad)
    document.removeEventListener("overlay:open", this.boundOverlayOpen)
    document.removeEventListener("click", this.boundOutsideClick, true)
    this.element.removeEventListener("click", this.boundDrawerLinkClick)
    this.#unlockScroll()
  }

  toggle(event) {
    event?.preventDefault()
    if (this.openValue) this.#close()
    else this.#open()
  }

  close(event) {
    event?.preventDefault()
    this.#close()
  }

  openValueChanged() {
    this.#syncDom()
  }

  #open() {
    document.dispatchEvent(
      new CustomEvent("overlay:open", { detail: { source: "drawer" }, bubbles: true })
    )
    this.openValue = true
    this.#lockScroll()
    queueMicrotask(() => {
      document.addEventListener("click", this.boundOutsideClick, true)
      this.closeButtonTarget?.focus()
    })
  }

  #close() {
    if (!this.openValue) return
    this.openValue = false
    document.removeEventListener("click", this.boundOutsideClick, true)
    this.#unlockScroll()
    this.menuButtonTarget?.focus()
  }

  #onOutsideClick(event) {
    if (!this.openValue) return

    const target = event.target
    if (this.hasDrawerTarget && this.drawerTarget.contains(target)) return
    if (this.hasMenuButtonTarget && this.menuButtonTarget.contains(target)) return

    this.#close()
  }

  #onEscape(event) {
    if (event.key === "Escape" && this.openValue) {
      event.preventDefault()
      this.#close()
    }
  }

  #onOverlayOpen(event) {
    if (event.detail?.source !== "drawer") this.#close()
  }

  #onDrawerLinkClick(event) {
    const link = event.target.closest("a[href]")
    if (!link || !this.drawerTarget.contains(link)) return
    this.#close()
  }

  #syncDom() {
    const open = this.openValue
    if (this.hasDrawerTarget) this.drawerTarget.dataset.mobileNavOpenValue = String(open)
    if (this.hasBackdropTarget) this.backdropTarget.dataset.mobileNavOpenValue = String(open)
    if (this.hasMenuButtonTarget) this.menuButtonTarget.setAttribute("aria-expanded", open ? "true" : "false")
    document.body.classList.toggle("overflow-hidden", open)
  }

  #lockScroll() {
    document.body.classList.add("overflow-hidden")
  }

  #unlockScroll() {
    if (!this.openValue) document.body.classList.remove("overflow-hidden")
  }
}
